import { Done } from '@mui/icons-material'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  BookingsResponseDto,
  CarrierVisitDirection,
  ContainerJourneyDto,
  NonNumericOrderDto,
  OrderResponseDto,
} from '@planning/app/api'
import { GateInViewStore } from '@planning/stores/gateControl/GateInViewStore'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { HoldEnforcementAlertWrapper } from '@planning/components/hold/HoldEnforcementAlertWrapper'
import { AlreadyOnTerminalAlert } from '../Atoms/AlreadyOnTerminalAlert'

import { ContainerAlreadyAssigned } from '../Atoms/ContainerAlreadyAssigned'
import { NoSpaceInExistingAllocationAlert } from '../Atoms/NoSpaceInExistingAllocationAlert'
import { OpenInboundWithDifferentCarrierAlert } from '../Atoms/OpenInboundWithDifferentCarrierAlert'
import { DropOffAutoCompleteRenderOption } from './DropOffAutoCompleteRenderOption'
import { GateInAutoComplete } from './GateInAutoComplete'
import { GeneralCargoOrderCard } from './GeneralCargoOrderCard'
import { IInspectContainerFormData } from './InspectContainer'
import { BookingSlotDialog } from './Organisms/BookingSlotDialog/BookingSlotDialog'
import { DropOffBookingCard } from './Organisms/DropOffBookingCard/DropOffBookingCard'

interface IProps {
  store: GateInViewStore
}

export const DropOffAutoComplete: FC<IProps> = observer(({ store }) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { tenantStore, dialogStore, appViewStore } = usePlanningStore()

  const [open, setOpen] = useState<boolean>(false)

  const dropOffSearchType = store.dropOffOrderSearchStore.searchType
  const dropOffContainerSearchType = store.dropOffOrderSearchStore.containerSearchType

  const isGeneralCargoOrder = (c: OrderResponseDto | ContainerJourneyDto) => {
    return 'commodityId' in c && c.commodityId !== null
  }

  const isBookingItem = (c: OrderResponseDto | ContainerJourneyDto | BookingsResponseDto) => {
    return 'id' in c === false
  }

  const openInboundForADifferentCarrierValidation = (
    order: ContainerJourneyDto | OrderResponseDto,
  ) => {
    return !!store.ordersByContainerNumber.find(
      item =>
        item.containerNumber === order.containerNumber &&
        item.direction === CarrierVisitDirection.Inbound &&
        item.carrierVisitId !== null,
    )
  }

  const alreadyOnTerminalValidation = (
    order: ContainerJourneyDto | OrderResponseDto | NonNumericOrderDto | BookingsResponseDto,
  ) => {
    if (order && !isBookingItem(order) && 'isOnTerminal' in order) {
      return (order as OrderResponseDto).isOnTerminal !== undefined && !!order.isOnTerminal
    }

    return false
  }

  const getOptions = () => {
    if (store.dropOffOrderSearchStore.searchType === 'generalCargo') {
      return store.dropOffOrderSearchStore.ordersForReferenceNumber
    }

    if (store.isSearchingByBookingNumber) {
      return store.dropOffOrderSearchStore.bookings
    }

    const orders = store.dropOffOrderSearchStore.orders

    return orders.filter(
      item =>
        !store.dropOffOrders.find(order => order.id === item.id) &&
        !store.dropOffGeneralCargoOrders.find(order => order.id === item.id),
    )
  }

  const getDuplicatedContainerAlertMessage = (order: ContainerJourneyDto | OrderResponseDto) => {
    if (store.isContainerFromOrderAlreadyAssigned(order)) {
      return <ContainerAlreadyAssigned />
    }

    return <></>
  }

  const getHoldsAlertMessage = (order: ContainerJourneyDto | OrderResponseDto) => {
    const holdCtx = store.isTruckAppointment && !store.truckVisitId ? 'TruckAppointment' : 'GateIn'
    return <HoldEnforcementAlertWrapper order={order} context={holdCtx} direction={CarrierVisitDirection.Inbound} />
  }

  const getAllocationAlertMessage = (order: ContainerJourneyDto | OrderResponseDto) => {
    if (store.dropOffOrderSearchStore.orderIdsWithNoAllocationSpace.includes(order.id))
      return <NoSpaceInExistingAllocationAlert />

    return <></>
  }

  const renderOption = (item: ContainerJourneyDto | OrderResponseDto | BookingsResponseDto) => {
    if (isBookingItem(item)) {
      const booking = item as BookingsResponseDto

      return (
        <DropOffBookingCard
          key={`booking-card-${booking.referenceNumber}-${booking.carrierVisit?.id}-${booking.containerOperator}`}
          booking={booking}
        />
      )
    } else {
      const dropOffOrder = item as ContainerJourneyDto | OrderResponseDto

      if (isGeneralCargoOrder(dropOffOrder) && dropOffSearchType === 'generalCargo') {
        return (
          <GeneralCargoOrderCard
            key={`order-card-${dropOffOrder.id}`}
            order={dropOffOrder as OrderResponseDto}
          />
        )
      }

      return (
        <DropOffAutoCompleteRenderOption
          key={`order-card-${dropOffOrder.id}`}
          dropOffOrder={{ ...dropOffOrder, customs: [] }}
          alertMessage={[
            getDuplicatedContainerAlertMessage(dropOffOrder),
            getAllocationAlertMessage(dropOffOrder),
            getHoldsAlertMessage(dropOffOrder),
            alreadyOnTerminalValidation(dropOffOrder) ? <AlreadyOnTerminalAlert /> : <></>,
            openInboundForADifferentCarrierValidation(dropOffOrder) ? (
              <OpenInboundWithDifferentCarrierAlert />
            ) : (
              <></>
            ),
          ]}
        />
      )
    }
  }

  const getOptionLabel = (item: ContainerJourneyDto | OrderResponseDto | BookingsResponseDto) => {
    if (store.isSearchingByBookingNumber) {
      return item.referenceNumber ?? ''
    }

    const order = item as ContainerJourneyDto | OrderResponseDto

    return order.containerNumber ?? order.referenceNumber ?? ''
  }

  const handleOnChange = (
    item: ContainerJourneyDto | OrderResponseDto | BookingsResponseDto | undefined,
  ) => {
    if (store.isSearchingByBookingNumber) {
      const booking = item as BookingsResponseDto

      if (!booking) return

      if (!booking.referenceNumber || !booking.containerOperator || !booking.carrierVisit?.id) {
        appViewStore.setShowAlert('error', t('bookingDetailsInvalid', 'Booking details invalid'))
        return
      }

      dialogStore.openDialog(
        <BookingSlotDialog
          bookingNumber={booking.referenceNumber}
          operator={booking.containerOperator}
          carrierVisitId={booking.carrierVisit.id}
          store={store}
        />,
      )

      return
    }

    const order = item as ContainerJourneyDto | OrderResponseDto

    if (order && isGeneralCargoOrder(order)) {
      store.upsertDropOffGeneralCargoOrder(order as IInspectContainerFormData)
    } else if (order) {
      store.upsertDropOffOrder(order as IInspectContainerFormData)
    }
  }

  const getPlaceHolder = () => {
    if (dropOffContainerSearchType === 'containerNumber')
      return t('searchUnitNumber', 'Search unit number')
    else if (dropOffContainerSearchType === 'referenceNumber')
      return t('searchReferenceNumber', 'Search reference number')
    else if (dropOffContainerSearchType === 'bookingNumber')
      return t('searchBookingNumber', 'Search booking number')

    return undefined
  }

  return (
    <>
      {tenantStore.hasGeneralCargo && (
        <Stack direction='row' spacing={1} mb={1}>
          <Chip
            icon={dropOffSearchType === 'container' ? <Done /> : <></>}
            variant={dropOffSearchType === 'container' ? 'filled' : 'outlined'}
            label={t('unit', 'Unit')}
            onClick={() => store.dropOffOrderSearchStore.setSearchType('container')}
          />
          <Chip
            icon={dropOffSearchType === 'generalCargo' ? <Done /> : <></>}
            label={t('generalCargo', 'General cargo')}
            variant={dropOffSearchType === 'generalCargo' ? 'filled' : 'outlined'}
            onClick={() => store.dropOffOrderSearchStore.setSearchType('generalCargo')}
          />
        </Stack>
      )}
      <GateInAutoComplete
        open={open}
        setOpen={setOpen}
        getOptionDisabled={option =>
          alreadyOnTerminalValidation(option) || store.isContainerFromOrderAlreadyAssigned(option)
        }
        placeholder={getPlaceHolder()}
        store={store.dropOffOrderSearchStore}
        setContainerSearchType={store.dropOffOrderSearchStore.setContainerSearchType}
        containerSearchType={store.dropOffOrderSearchStore.containerSearchType}
        searchType={store.dropOffOrderSearchStore.searchType}
        type={'DropOff'}
        options={getOptions()}
        renderOption={renderOption}
        getOptionLabel={getOptionLabel}
        handleOnChange={handleOnChange}
      />
      {store.dropOffOrderSearchStore.filter.trim() !== '' &&
        !store.dropOffOrderSearchStore.isLoading &&
        store.dropOffOrderSearchStore.isNoMatches && (
          <Box>
            <Stack
              gap={theme.customSpacing.xxs}
              direction={'row'}
              alignItems={'center'}
              sx={{
                backgroundColor: theme.palette.warning.lighter,
                color: theme.palette.warning.dark,
                padding: `${theme.customSpacing.xs}`,
                borderRadius: theme.customRadius.small,
                margin: `${theme.customSpacing.xs} 0`,
              }}
            >
              <Typography variant='caption'>
                {t(
                  'noExisitingOrderNewOrderMustBeCreated',
                  'There is no existing order for this container. A new order must be created.',
                )}
              </Typography>
            </Stack>
          </Box>
        )}
    </>
  )
})
